<!-- Copyright 2026, Common Good Learning Tools LLC -->
<template><k-dialog v-model="dialog_open" max-width="1100px" :fullscreen="fullscreen" :content-class="dialog_class" persistent scrollable>
<k-card>
	<k-card-title v-if="!opened_from_embedded" class="black white--text py-2" style="border-bottom:1px solid #999">
		<k-icon color="#fff" class="mr-3" style="font-size:30px;">fas fa-cubes</k-icon>
		<b>Satchel Competency-Resource Alignment System: SCRAM “Job”</b>

		<k-spacer/>

		<k-btn v-if="!opened_from_embedded" color="#666" dark fab x-small accessible-label="Close resource alignment tool" @click="close_interface"><k-icon>fas fa-xmark</k-icon></k-btn>

	</k-card-title>
	<k-card-text class="k-scram-job-card" style="font-size:14px; line-height:17px"
		v-if='initialized'
		:style="!opened_from_embedded?'border-top:1px solid #999':''"
	>

	<!-- ============================================= -->
	<!-- Job ID and Description -->
		<div class="mt-5 d-flex align-start" style="font-size:18px;"
		>
			<div class="mr-5 mt-2"
				v-if="cram_job_id"
			>
				<b>Job ID:</b> {{cram_job_id}}
			</div>

			<div class="mr-2 mt-2">
				<b>Description:</b>
			</div>

			<div style="width:700px">
				<k-textarea outlined dense hide-details placeholder="" rows="2" auto-grow
					aria-label="Description"
					v-model="job_description"
				></k-textarea>
			</div>
		</div>

	<!-- ============================================= -->
	<!-- Resource Universe -->
		<div class="mt-5">
			<div class="d-flex align-center">
				<h3 class="mr-3">
					<nobr>Resource Universe:</nobr>
				</h3>

				<k-spacer v-if="ru.CASEResourceFrameworkIdentifier" />

				<FrameworkSwitcher2 color="primary" 
					btn_size="large"
					:current_selected_framework_identifier="ru.CASEResourceFrameworkIdentifier" 
					:large_btn_content="framework_switcher_btn_content"
					:framework_record_list="framework_switcher_framework_records"
					@framework_selected="set_resource_framework" 
				/>
			</div>

			<div class="d-flex align-center ml-8 mt-3"
				v-if="ru.CASEResourceFrameworkIdentifier"
			>
				<div class="k-scram-job-framework-title mr-2"
					v-html="framework_title_display(ru_fr)"
				></div>

				<k-btn small color="primary" class="mr-2 k-tight-btn k-nocaps-btn"
					@click="choose_resource_branches"
				>Choose Branches</k-btn>
				
				<div class="k-scram-job-branch-title mr-2"
					v-if="ru.CASEGroupIdentifiers[0]"
					v-html="branch_title_display(ru_fr, ru.CASEGroupIdentifiers[0])"
				></div>

				<div style="width:240px">
					<k-select background-color="#fff" outlined densex hide-details
						multiple
						label="Resource Types"
						v-model="ru.CFItemTypes" 
						:items="item_type_list(ru_fr)"
						:menu-props="{ top: true, dense: true }"
					></k-select>
				</div>

				<k-spacer/>

				<k-text-field class="k-scram-job-guid-field mr-2" outlined dense hide-details background-color="#fff"
					v-if="show_identifier_text_fields"
					label="Framework GUID" 
					v-model="ru.CASEResourceFrameworkIdentifier"
				></k-text-field>

				<k-text-field class="k-scram-job-guid-field mr-0" outlined dense hide-details background-color="#fff"
					v-if="show_identifier_text_fields"
					label="Branch GUID" 
					v-model="ru.CASEGroupIdentifiers[0]"
				></k-text-field>
			</div>

			<div class="d-flex align-center ml-8 mt-3"
				v-if="ru.CASEResourceFrameworkIdentifier"
			>
				<div>Item type to evaluate via aggregation:</div>

				<div class="ml-2" style="width:240px">
					<k-select background-color="#fff" outlined dense hide-details
						v-model="payload.resourceAggregationItemType" 
						:items="aggregation_item_type_list()"
						:menu-props="{ top: true, dense: true }"
					></k-select>
				</div>
			</div>
		</div>

	<!-- ============================================= -->
	<!-- Competency Universe -->
		<div class="mt-5">
			<h3><nobr>Competency Universe:</nobr></h3>

			<div class="d-flex align-center ml-8 mt-3">
				<div class="k-scram-job-framework-title mr-2"
					v-if="cu.frameworkIdentifier"
					v-html="framework_title_display(cu_fr)"
				></div>
				
				<div class="k-scram-job-branch-title mr-2"
					v-if="cu.itemIdentifiers[0]"
					v-html="branch_title_display(cu_fr, cu.itemIdentifiers[0])"
				></div>

				<div style="width:240px">
					<k-select background-color="#fff" outlined densex hide-details
						multiple
						label="Competency Types"
						v-model="cu.CFItemTypes" 
						@change="cu_item_types_changed(cu)"
						:items="cu_item_type_list(cu_fr)"
						:menu-props="{ top: true, dense: true }"
					></k-select>
				</div>

				<k-spacer/>

				<k-text-field class="k-scram-job-guid-field mr-2" outlined dense hide-details background-color="#fff"
					v-if="show_identifier_text_fields"
					label="Framework GUID" 
					v-model="cu.frameworkIdentifier"
				></k-text-field>

				<k-text-field class="k-scram-job-guid-field mr-0" outlined dense hide-details background-color="#fff"
					v-if="show_identifier_text_fields"
					label="Branch GUID" 
					v-model="cu.itemIdentifiers[0]"
				></k-text-field>
			</div>

			<div class="d-flex align-center ml-8 mt-3">
				<div>Competency education levels:</div>

				<div class="ml-2" style="width:200px">
					<k-select background-color="#fff" outlined dense hide-details
						multiple
						v-model="cu.educationLevel" 
						:items="education_level_list()"
						:menu-props="{ top: true, dense: true }"
					></k-select>
				</div>

				<div class="ml-5">Education level (EL) match tolerance:</div>

				<div class="ml-2" style="width:320px">
					<k-select background-color="#fff" outlined dense hide-details
						multiple
						v-model="payload.educationLevelMatchTolerance" 
						:items="education_level_tolerance_list()"
						:menu-props="{ top: true, dense: true }"
					></k-select>
				</div>
			</div>
		</div>

	<!-- ============================================= -->
	<!-- Controls for using this SCRAM job -->
		
		<!-- If job not started, or changed, button to Evaluate -->
		<div class="mt-5 pt-3 d-flex justify-center" style="border-top:1px solid #999"
			v-if="worker_status_includes(['unknown','not_started'])"
		>
			<k-btn color="primary" class="mx-2"
				v-if="save_job_available"
				@click="save_job"
			><k-icon smallX class="mr-2">fas fa-save</k-icon> Save Job</k-btn>

			<k-btn color="green darken-3" dark class="mx-2 elevation-0"
				v-if="!save_job_available"
				@click="save_job"
			><k-icon smallX class="mr-2">fas fa-circle-check</k-icon> Job Saved</k-btn>

			<k-btn color="primary"
				@click="evaluate_params"
			>Evaluate SCRAM Job <k-icon smallX class="ml-2">fas fa-arrow-right</k-icon></k-btn>
		</div>

		<!-- Matrix stats / status / processing controls -->
		<div class="k-scram-job-status mt-5"
			v-if="!worker_status_includes(['unknown'])"
		>
			<div class="d-flex justify-center">
				<div class="ml-0">Resource Universe Count: <b>{{matrix_stats.resourceCount}}</b></div>
				<div class="ml-5">Competency Universe Count: <b>{{matrix_stats.competencyCount}}</b></div>
				<div class="ml-5">Matrix Size: <b>{{matrix_stats.matrixCellCount}}</b></div>
			</div>

			<div class="d-flex justify-center mt-3"
				v-if="worker_needs_to_run && !worker_status_includes(['processing'])"
			>
				<div class="ml-0">Matrix cells needed to calculate: <b>{{matrix_stats.missingGenAICount}}</b></div>
			</div>

			<div class="d-flex justify-center align-center mt-3"
				v-if="!worker_needs_to_run"
			>
				<div class="ml-0">
					<k-icon color="green darken-3" class="mr-1">fas fa-circle-check</k-icon>
					All Matrix cells calculated
				</div>
			</div>

			<!-- progress bar for running genai -->
			<div class="d-flex justify-center align-center mt-3" style="font-size:16px;"
				v-if="worker_needs_to_run"
			>
				<div class="mr-2">Matrix cells processed: <b>{{cells_processed}}</b></div>
				<div style="width:400px">
					<k-progress-linear height="32" color="green" rounded
						:stream="worker_status_includes(['processing', 'cancel_requested'])"
						:buffer-value="worker_status_includes(['processing', 'cancel_requested']) ? 0 : 100"
						v-model="cells_processed_pct" 
					>
						<template v-slot:default="{ value }">
							<strong>{{ Math.ceil(value) }}%</strong>
						</template>
					</k-progress-linear>
				</div>
				<div class="ml-2"><b>{{matrix_stats.matrixCellCount}}</b></div>
			</div>

			<!-- buttons for controlling genai processing -->
			<div class="d-flex justify-center align-center mt-5"
				v-if="worker_needs_to_run"
			>
				<k-btn color="secondary" class="mx-2 k-tight-btn k-nocaps-btn"
					v-if="!worker_status_includes(['queued', 'processing']) && worker_needs_to_run"
					@click="evaluate_params"
				><k-icon small class="mr-2">fas fa-arrows-rotate</k-icon>Re-Evaluate Parameters</k-btn>

				<k-btn color="red darken-4" class="mx-2 k-tight-btn k-nocaps-btn" dark
					v-if="worker_status == 'processing'"
					@click="cancel_worker_job"
				><k-icon small class="mr-2">fas fa-pause</k-icon> Pause Worker Job</k-btn>
			</div>

		</div>

		<!-- action buttons on the job: show if we have a cram_job_id -->
		<div class="mt-5 d-flex justify-center"
			v-if="job_status"
		>
			<k-btn color="primary" class="mx-2"
				v-if="save_job_available"
				@click="save_job"
			><k-icon smallX class="mr-2">fas fa-save</k-icon> Save Job</k-btn>

			<k-btn color="green darken-3" dark class="mx-2 elevation-0"
				v-if="!save_job_available"
				@click="save_job"
			><k-icon smallX class="mr-2">fas fa-circle-check</k-icon> Job Saved</k-btn>

			<k-btn color="primary" class="mx-2"
				v-if="worker_needs_to_run"
				:disabled="worker_status_includes(['queued', 'processing'])"
				@click="run_worker_job"
			><k-icon smallX class="mr-2">fas fa-person-running</k-icon> Run SCRAM Worker Job</k-btn>

			<k-btn color="primary" class="mx-2"
				:disabled="!launch_grid_view_available"
				@click="launch_grid_view"
			><k-icon smallX class="mr-2">fas fa-grid</k-icon> Launch Grid View</k-btn>

		</div>

		<!-- Details of job status -->
		<div class="mt-5 py-2 d-flex justify-center grey lighten-3" style="border-radius:8px"
			v-if="job_status"
		>
			<div class="mx-2">
				Worker status: <b>{{worker_status}}</b>
			</div>
			<div class="mx-2" v-if="updated_at_display">
				Last updated: <b>{{updated_at_display}}</b>
			</div>
			<div class="mx-2" v-if="job_status.job_error">
				JOB ERROR: <b>{{job_status.job_error}}</b>
			</div>
			<div class="mx-2" v-if="job_status.token_cost>0">
				Token cost: <b>${{job_status.token_cost.toFixed(4)}}</b>
			</div>
			<div class="mx-2" v-if="job_status.genai_time_spent>0">
				GenAI duration: <b>{{time_spent_display}}</b>
			</div>
		</div>

	</k-card-text>
</k-card>
</k-dialog></template>

<script>
import { mapState, mapGetters } from 'vuex'
import FrameworkSwitcher2 from '../frameworks/FrameworkSwitcher2'

export default {
	components: { FrameworkSwitcher2 },
	props: {
		// params: object, must include at least cram_job_id, which can be 0 if we're creating a new job
		params: { required: true },
	},
	data() { return {
		dialog_open: true,
		initialized: false,
		show_identifier_text_fields: false,
		payload: null,

		cwj_timeout_duration__processing: 500,
		cwj_timeout_duration__default: 5000,

		job_description: '',
		job_status: null,
	}},
	computed: {
		...mapState(['site_config', 'framework_records', 'user_info']),
		...mapGetters([]),

		framework_switcher_btn_content() {
			return `<i class="fas fa-cubes mr-2""></i>${this.ru.CASEResourceFrameworkIdentifier ? 'Change' : 'Choose'} Resource Framework…`
		},

		framework_switcher_framework_records() {
			return this.framework_records.filter(x=>{
				return x.json.CFDocument.frameworkType == 'Resource Collection'
			})
		},

		cram_job_id() {
			return this.params.cram_job_id
		},	

		worker_status() { 
			return this.job_status?.worker_status || 'unknown'
		},

		matrix_stats() {
			return this.job_status?.matrix_stats ?? {}
		},
		ws_stats() {
			return this.job_status?.worker_status_stats ?? {}
		},

		worker_needs_to_run() {
			return this.matrix_stats.missingGenAICount > 0
		},

		updated_at_display() {
			if (empty(this.job_status?.updated_at)) return ''
			return date.format(U.convert_to_local_date(this.job_status.updated_at), 'M/D/YY h:mm A')
		},

		time_spent_display() {
			if (empty(this.job_status)) return ''
			// first convert genai_time_spent from ms to seconds
			let sec = Math.round(this.job_status.genai_time_spent / 1000)
			if (sec < 1) {
				return '< 1 sec'
			}

			// separate into number of minutes and seconds, then format rv
			let min = Math.floor(sec / 60)
			sec = (sec % 60)
			if (sec*1 < 10) sec = '0' + sec
			return `${min}:${sec}`
		},

		cells_processed() {
			if (empty(this.ws_stats) || empty(this.matrix_stats)) return 0
			return this.matrix_stats.matrixCellCount - this.matrix_stats.missingGenAICount
		},

		cells_processed_pct() {
			if (empty(this.ws_stats) || empty(this.matrix_stats)) return 0
			if (this.matrix_stats.matrixCellCount == 0) return 0
			return Math.floor(this.cells_processed / this.matrix_stats.matrixCellCount * 100)
		},

		// shortcut to first competency universe item / resource universe item
		cu() { return this.payload.competencyUniverse[0] },
		ru() { return this.payload.resourceUniverse[0] },

		cu_fr() { 
			let fid = this.cu.frameworkIdentifier
			
			if (empty(fid)) return null

			let fr = this.framework_records.find(x=>x.lsdoc_identifier == fid)
			if (empty(fr)) return null	// we apparently don't know anything about this framework
			// load the framework if necessary
			U.load_framework_and_cfo(fid, {show_loader: true}).then(fr=>{
				// do something when framework is loaded?
			})
			return fr
		},
		
		ru_fr() { 
			let fid = this.ru.CASEResourceFrameworkIdentifier
			
			if (empty(fid)) return null

			let fr = this.framework_records.find(x=>x.lsdoc_identifier == fid)
			if (empty(fr)) return null	// we apparently don't know anything about this framework
			// load the framework if necessary
			U.load_framework_and_cfo(fid, {show_loader: true}).then(fr=>{
				// do something when framework is loaded?
			})
			return fr
		},
		
		opened_from_embedded() {
			return this.params?.opened_from_embedded == true
		},	

		fullscreen() {
			return false
		},

		dialog_class() {
			let s = 'k-scram-job-dialog'

			if (this.opened_from_embedded) {
				s += ' k-scram-job-dialog-embedded'
			}

			return s
		},

		job_parameters_changed() {
			if (empty(this.payload) || empty(this.job_status?.universe_params)) {
				return true
			}

			// make sure params are in the right order for params and job_status.universe_params
			let saved_payload = JSON.stringify({
				resourceUniverse: this.job_status.universe_params.resourceUniverse,
				competencyUniverse: this.job_status.universe_params.competencyUniverse,
				resourceAggregationItemType: this.job_status.universe_params.resourceAggregationItemType,
				educationLevelMatchTolerance: this.job_status.universe_params.educationLevelMatchTolerance,
				job_description: (this.job_status?.job_description ?? '')
			})

			let current_payload = JSON.stringify({
				// the incoming job_status.payload will have xUniverse params stringified, so we need to do the same here
				resourceUniverse: JSON.stringify(this.payload.resourceUniverse),
				competencyUniverse: JSON.stringify(this.payload.competencyUniverse),
				resourceAggregationItemType: this.payload.resourceAggregationItemType,
				educationLevelMatchTolerance: this.payload.educationLevelMatchTolerance,
				job_description: this.job_description,
			})

			return !(saved_payload == current_payload)
		},

		save_job_available() {
			if (this.worker_status_includes(['queued', 'processing'])) return false
			if (!this.job_parameters_changed) return false
			return true
		},

		launch_grid_view_available() {
			if (this.worker_status_includes(['queued', 'processing'])) return false
			if (this.worker_needs_to_run) return false
			if (this.save_job_available) return false
			return true
		},

	},
	watch: {
		params: { deep:true, immediate: true, handler() {
			// console.warn('PARAMS WATCHER')
		}},

		worker_status: { immediate: true, handler() {
			// console.warn('status changed to ' + this.worker_status)
		}},

	},
	created() {
	},
	async mounted() {
		console.warn('mounting SCRAMJobView: ' + this.params.cram_job_id)
		vapp.scram_job_component = this

		// if we have a cram_job_id we're starting with, get initial status
		// this will fill in the payload if universe_params are saved with the job
		if (this.cram_job_id > 0) {
			await this.get_job_status()
		}

		// if payload is still null, create default payload to use
		if (this.payload === null) {
			this.create_ui_payload()
		}

		this.initialized = true
	},
	beforeDestroy() {
		clearTimeout(this.cwj_timeout)
	},
	methods: {
		get_default_payload() {
			return {
				resourceUniverse: [
					{
						CASEResourceFrameworkIdentifier: '',
						CASEGroupIdentifiers: [],
						CFItemTypes: ['Learning Resource'],
					}
				],
				/* alt resourceUniverse: 
				resourceUniverse: [
					{
						resourceSourcedId: '',		// '12345'
						title: '',
						alignmentMetadata: [''],
						educationLevel: [],
					}, ...
				], ...
				*/
				competencyUniverse: [
					{
						frameworkIdentifier: '',
						itemIdentifiers: [],
						alignToBranches: true,		// identifiersAreBranches? use groupIdentifiers instead of itemIdentifiers?
						outcomesOnly: false,
						outcomeLeafsOnly: true,
						CFItemTypes: [],
						educationLevel: [],
					}
				],
				matchCountToReturn: 10,
				returnedCFItemData: 'base',
				searchTerms: '',

				educationLevelMatchTolerance: -1,
				resourceAggregationItemType: '',	// Item type to evaluate via aggregation (e.g. chapters/sections)

				alignmentService: '',

				action: '',
			}
		},

		get_demo_payload() {
			let p = this.get_default_payload()
			p.resourceUniverse[0].CASEResourceFrameworkIdentifier = 'd51823e4-b8bd-4990-9e02-2c3ce7fafb3e'
			p.resourceUniverse[0].CASEGroupIdentifiers = ['e3f26c6a-25c9-466f-a3bf-3753d765c3cf']
			p.resourceUniverse[0].CFItemTypes = ['Section']
			p.resourceUniverse = JSON.stringify(p.resourceUniverse)

			// VA Science 7th grade
			p.competencyUniverse[0].frameworkIdentifier = 'f228b6ac-1313-11eb-80c4-0242c0a84003'
			p.competencyUniverse[0].itemIdentifiers = ['b223b403-6caf-48f5-9b49-013b06aedacd']
			// p.competencyUniverse[0].CFItemTypes = ['Standard Element']
			p.competencyUniverse[0].CFItemTypes = ['Standard']
			p.competencyUniverse = JSON.stringify(p.competencyUniverse)

			p.resourceAggregationItemType = 'Chapter'

			return p
		},

		get_demo_payload2() {
			let p = this.get_default_payload()
			p.resourceUniverse[0].CASEResourceFrameworkIdentifier = 'd51823e4-b8bd-4990-9e02-2c3ce7fafb3e'
			p.resourceUniverse[0].CASEGroupIdentifiers = ['e3f26c6a-25c9-466f-a3bf-3753d765c3cf']
			p.resourceUniverse[0].CFItemTypes = ['Section']
			p.resourceUniverse = JSON.stringify(p.resourceUniverse)

			// IL Science
			p.competencyUniverse[0].frameworkIdentifier = '189719e9-90f1-4af4-b8fb-d74d12e5c4e7'
			p.competencyUniverse[0].itemIdentifiers = [ 'b5554b98-282b-423e-8209-8c4ec0c860c2', 'ee39a5c5-8b48-4185-aa5a-7da82fc70a6a', '9e59f8b1-f100-4734-ad7b-5c1d5c806367', '71e8466c-006a-4c12-9de9-d05a003bed38', ]
			p.competencyUniverse[0].CFItemTypes = ['DCI Element']
			p.competencyUniverse = JSON.stringify(p.competencyUniverse)

			p.resourceAggregationItemType = 'Chapter'

			return p
		},

		get_demo_payload3() {
			let p = this.get_default_payload()
			p.resourceUniverse[0].CASEResourceFrameworkIdentifier = 'd51823e4-b8bd-4990-9e02-2c3ce7fafb3e'
			p.resourceUniverse[0].CASEGroupIdentifiers = ['e3f26c6a-25c9-466f-a3bf-3753d765c3cf']
			p.resourceUniverse[0].CFItemTypes = ['Section']
			p.resourceUniverse = JSON.stringify(p.resourceUniverse)

			// CA Science, grades 6-8 (Discipline Specific Model Standards)
			p.competencyUniverse[0].frameworkIdentifier = 'd2ef5b18-b034-43fa-aeeb-03357e651070'
			p.competencyUniverse[0].itemIdentifiers = [ '9ea0def5-76d9-4234-a000-61b177b448b9', ]
			p.competencyUniverse[0].CFItemTypes = ['Performance Expectation']
			p.competencyUniverse = JSON.stringify(p.competencyUniverse)

			p.resourceAggregationItemType = 'Chapter'

			return p
		},

		// Exploring Science (6th Grade) - TE + Activities/Labs
		get_demo_payload4() {
			let p = this.get_default_payload()
			p.resourceUniverse[0].CASEResourceFrameworkIdentifier = '073385a8-48e1-4b9b-9ea0-3578dbd4fd87'
			p.resourceUniverse[0].CASEGroupIdentifiers = ['32b82822-182b-4b96-a3f2-1c060a26a7be', 'f8cdb45d-85ad-4372-96ee-50182bf7d9e2', '8d6b2253-6553-4f9e-ac54-a081e1ce9727']
			p.resourceUniverse[0].CFItemTypes = ['Section']
			p.resourceUniverse = JSON.stringify(p.resourceUniverse)

			// CA Science, grades 6-8 (Discipline Specific Model Standards)
			p.competencyUniverse[0].frameworkIdentifier = 'd2ef5b18-b034-43fa-aeeb-03357e651070'
			p.competencyUniverse[0].itemIdentifiers = [ '9ea0def5-76d9-4234-a000-61b177b448b9', ]
			p.competencyUniverse[0].CFItemTypes = ['Performance Expectation']
			p.competencyUniverse = JSON.stringify(p.competencyUniverse)

			p.resourceAggregationItemType = 'Chapter'

			return p
		},

		// Life Science (7th Grade) -- TE + Activities/Labs
		get_demo_payload5() {
			let p = this.get_default_payload()
			p.resourceUniverse[0].CASEResourceFrameworkIdentifier = '57e16957-3305-4378-9dce-5c6c7a873195'
			p.resourceUniverse[0].CASEGroupIdentifiers = ['cdaf85ff-277c-4ab5-bcf5-13af12c63d2d', '96e10b5f-7c6c-4100-abc3-12d9ed3392ea']
			p.resourceUniverse[0].CFItemTypes = ['Section']
			p.resourceUniverse = JSON.stringify(p.resourceUniverse)

			// CA Science, grades 6-8 (Discipline Specific Model Standards)
			p.competencyUniverse[0].frameworkIdentifier = 'd2ef5b18-b034-43fa-aeeb-03357e651070'
			p.competencyUniverse[0].itemIdentifiers = [ '9ea0def5-76d9-4234-a000-61b177b448b9', ]
			p.competencyUniverse[0].CFItemTypes = ['Performance Expectation']
			p.competencyUniverse = JSON.stringify(p.competencyUniverse)

			p.resourceAggregationItemType = 'Chapter'

			return p
		},

		// Earth & Space Science (8th Grade) -- TE + Labs
		get_demo_payload6() {
			let p = this.get_default_payload()
			p.resourceUniverse[0].CASEResourceFrameworkIdentifier = '4bd997aa-9b6c-4b40-96e3-e46fdbf77e81'
			p.resourceUniverse[0].CASEGroupIdentifiers = ['fa328784-1e23-4d36-9a3f-673811230c75', '1e3d14c8-c9bd-4efb-90d7-b1c24e9c97ec']
			p.resourceUniverse[0].CFItemTypes = ['Section']
			p.resourceUniverse = JSON.stringify(p.resourceUniverse)

			// CA Science, grades 6-8 (Discipline Specific Model Standards)
			p.competencyUniverse[0].frameworkIdentifier = 'd2ef5b18-b034-43fa-aeeb-03357e651070'
			p.competencyUniverse[0].itemIdentifiers = [ '9ea0def5-76d9-4234-a000-61b177b448b9', ]
			p.competencyUniverse[0].CFItemTypes = ['Performance Expectation']
			p.competencyUniverse = JSON.stringify(p.competencyUniverse)

			p.resourceAggregationItemType = 'Chapter'

			return p
		},

		// Combined courses
		get_demo_payload7() {
			let p = this.get_default_payload()
			p.resourceUniverse[0].CASEResourceFrameworkIdentifier = '57e16957-3305-4378-9dce-5c6c7a873195'
			p.resourceUniverse[0].CASEGroupIdentifiers = ['1007743b-fc20-4c29-af7a-3066ce0b74ba', '22bd887f-8046-455e-84a4-7d8f7bee93f0', 'e765ba1c-4901-4d28-b805-45d77ec545cc']
			p.resourceUniverse[0].CFItemTypes = ['Section']
			p.resourceUniverse = JSON.stringify(p.resourceUniverse)

			// CA Science, grades 6-8 (Discipline Specific Model Standards)
			p.competencyUniverse[0].frameworkIdentifier = 'd2ef5b18-b034-43fa-aeeb-03357e651070'
			p.competencyUniverse[0].itemIdentifiers = [ '9ea0def5-76d9-4234-a000-61b177b448b9', ]
			p.competencyUniverse[0].CFItemTypes = ['Performance Expectation']
			p.competencyUniverse = JSON.stringify(p.competencyUniverse)

			p.resourceAggregationItemType = 'Chapter'

			return p
		},

		create_ui_payload(received_payload) {
			if (empty(received_payload)) {
				received_payload = {}
				// DEBUG: use demo payload...
				// received_payload = this.get_demo_payload7()
			}
			// make sure received_payload is "pure" (not reactive)
			received_payload = sc(received_payload)

			// start with default_payload; fill in with any values from received_payload
			// note that we might need to do something more sophisticated in the future if we fiddle with the format of the universe definitions...
			let p = this.get_default_payload()
			for (let key in received_payload) {
				// the xxYniverse fields come in json-encoded
				if (key == 'resourceUniverse' || key == 'competencyUniverse') {
					p[key] = JSON.parse(received_payload[key])
				} else {
					p[key] = received_payload[key]
				}
			}

			// for the UI, we combine CFItemTypes, outcomesOnly, and outcomeLeafsOnly
			let cu = p.competencyUniverse[0]
			if (cu.CFItemTypes.length == 0) {
				if (cu.outcomesOnly) cu.CFItemTypes = ['all_outcomes']
				else if (cu.outcomeLeafsOnly) cu.CFItemTypes = ['all_leafs']
			}

			this.payload = p
		},

		get_service_payload() {
			let payload = {}

			if (this.payload != null) {
				payload = sc(this.payload)

				// reverse CFItemTypes translation above
				let cu = payload.competencyUniverse[0]
				if (cu.CFItemTypes[0] == 'all_outcomes') {
					cu.CFItemTypes = []
					cu.outcomesOnly = true
					cu.outcomeLeafsOnly = false
				} else if (cu.CFItemTypes[0] == 'all_leafs') {
					cu.CFItemTypes = []
					cu.outcomesOnly = false
					cu.outcomeLeafsOnly = true
				} else {
					cu.outcomesOnly = false
					cu.outcomeLeafsOnly = false
				}
				
				// stringify universes
				payload.resourceUniverse = JSON.stringify(payload.resourceUniverse)
				payload.competencyUniverse = JSON.stringify(payload.competencyUniverse)
			}

			// add user_id
			payload.user_id = this.user_info.user_id

			// add cram_job_id if we have it
			if (!empty(this.cram_job_id)) {
				payload.cram_job_id = this.cram_job_id
				// add job_description too
				payload.job_description = this.job_description
			}

			return payload
		},

		////////////////////////////////
		async set_resource_framework(selected_framework_identifier) {
			// if the RU was empty, we just need to set this
			if (empty(this.ru.CASEResourceFrameworkIdentifier)) {
				this.ru.CASEResourceFrameworkIdentifier = selected_framework_identifier
				return
			}

			// TODO: make sure the user really wants to change this, or remember previous settings
			this.ru.CASEResourceFrameworkIdentifier = selected_framework_identifier
			this.ru.CASEGroupIdentifiers = []
			this.ru.CFItemTypes = ['Learning Resource']
		},

		choose_resource_branches() {

		},

		////////////////////////////////
		framework_title_display(fr) {
			if (empty(fr)) return 'Framework Title'
			return fr.json.CFDocument.title
		},

		branch_title_display(fr, id) {
			if (empty(fr)) return 'Branch Title'
			let cfi = fr?.cfo?.cfitems[id]
			if (empty(cfi)) return '<i>UNKNOWN BRANCH</i>'
			
			let s = cfi.fullStatement
			if (cfi.humanCodingScheme) s = `<b>${cfi.humanCodingScheme}</b> ${s}`
			return s
		},

		item_type_list(fr) {
			if (empty(fr?.cfo?.item_types)) return []

			let arr = []
			for (let t of fr.cfo.item_types) {
				arr.push({ value: t, text: t })
			}
			return arr
		},

		aggregation_item_type_list() {
			let arr = this.item_type_list(this.ru_fr)
			arr.unshift({ value: '', text: 'NONE (no aggregation)' })
			return arr
		},

		cu_item_type_list(fr) {
			let arr = this.item_type_list(fr)
			arr.unshift({ value: 'all_outcomes', text: 'ALL “OUTCOMES”' })
			arr.unshift({ value: 'all_leafs', text: 'ALL “LEAFS”' })
			return arr
		},

		cu_item_types_changed(cu) {
			// if we have two or more items selected...
			if (cu.CFItemTypes.length >= 2) {
				// if the user just chose 'all_leafs' or 'all_outcomes', just choose that option
				if (cu.CFItemTypes[cu.CFItemTypes.length-1] == 'all_leafs') {
					cu.CFItemTypes = ['all_leafs']
				} else if (cu.CFItemTypes[cu.CFItemTypes.length-1] == 'all_outcomes') {
					cu.CFItemTypes = ['all_outcomes']

				// else if the first item is one of these, shift it off the list
				} else if (cu.CFItemTypes[0] == 'all_leafs' || cu.CFItemTypes[0] == 'all_outcomes') {
					cu.CFItemTypes.shift()
				}
			}
		},

		education_level_list() {
			let arr = []
			for (let i = 1; i < this.$store.state.grades.length; ++i) {
				arr.push({
					value: this.$store.state.grades[i].value,
					text: this.$store.state.grades[i].text,
				})
			}
			return arr
		},

		education_level_tolerance_list() {
			let arr = [
				{ value:-1, text: 'Ignore EL when considering matches' },
				{ value:0, text: 'Consider exact EL matches only' },
				{ value:1, text: 'Allow for EL mismatches of ± 1 grade level' },
				{ value:2, text: 'Allow for EL mismatches of ± 2 grade levels' },
				{ value:3, text: 'Allow for EL mismatches of ± 3 grade levels' },
			]
			return arr
		},

		////////////////////////////////////////////////////////////////
		async execute_job(action) {
			let payload = this.get_service_payload()
			payload.action = action

			// if (action != 'get_job_status') U.loading_start()
			let result = await U.ajax_promise('align/scram', payload)
			// U.loading_stop()

			if (result.status != 'ok') {
				this.$alert('ERROR: ' + result.status)
				return
			}

			// console.log(`execute_job (${action}) / NOW ${result.job_status.worker_status} == WAS ${this.worker_status}`)

			// if status changed from 'processing' or 'cancel_requested' to something else, immediately run evaluate_params
			if (this.worker_status_includes(['processing', 'cancel_requested']) && result.job_status.worker_status != this.worker_status) {
				this.$nextTick(x=>this.evaluate_params());
			}

			// we will always get back job_status, but if we haven't yet saved the job, it won't include a cram_job_id
			this.job_status = result.job_status

			// if we do get a cram_job_id...
			if (!empty(this.job_status.cram_job_id)) {
				// set cram_job_id if we just got it
				// TODO: set this in store or vapp or something??
				if (this.job_status.cram_job_id != this.cram_job_id) {
					// console.warn('setting cram_job_id to ' + this.cram_job_id)
					this.cram_job_id = this.job_status.cram_job_id
				}

				// if this.payload is null and we received universe_params, create payload
				if (this.payload === null && !empty(this.job_status.universe_params)) {
					this.create_ui_payload(this.job_status.universe_params)
				}
			}

			return result
		},

		async evaluate_params() {
			await(this.execute_job('evaluate_params'))
		},

		async run_worker_job() {
			// start the job running, then immediately start checking
			await(this.execute_job('run_worker_job'))
			this.$nextTick(x=>this.get_job_status())
		},

		async get_job_status() {
			clearTimeout(this.cwj_timeout)
			await(this.execute_job('get_job_status'))

			// set incoming job_description
			this.job_description = this.job_status?.job_description ?? ''

			// automatically re-check again after a delay, depending on the status
			let delay 
			if (this.worker_status_includes(['queued', 'processing', 'cancel_requested'])) delay = this.cwj_timeout_duration__processing
			else delay = this.cwj_timeout_duration__default

			// for now at least, only auto-check if we're actually running a worker job
			if (delay > this.cwj_timeout_duration__processing) return

			this.cwj_timeout = setTimeout(x=>{
				// console.log('auto-get_job_status / last_status: ' + this.worker_status)
				this.get_job_status()
			}, delay)
		},

		async cancel_worker_job() {
			await(this.execute_job('cancel_worker_job'))
		},

		////////////////////////////////////////////////////////////////
		worker_status_includes(arr) {
			if (typeof(arr) == 'string') arr = [arr]
			return arr.includes(this.worker_status)
		},

		async save_job() {
			await(this.execute_job('save_job'))
		},

		async launch_grid_view() {
			let url = `/crm/${this.cram_job_id}`
			window.open(url, '_blank')
		},

		////////////////////////////////////////////////////////////////
		close_interface() {
			if (false) {
				vapp.$refs.satchel.execute('hide')
			}

			// update vapp.scram_manager_update_id to force the SCRAMManager to reload jobs
			vapp.scram_manager_update_id += 1
			this.$emit('dialog_cancel')
		},
	}
}
</script>

<style lang="scss">
.k-scram-job-dialog {
	.v-card {
		background-color:#fff;
		border-radius:0!important;
		border:#000 4px solid;
	}

	.v-card__text, .v-card__title {
		padding-left:16px!important;
		padding-right:16px!important;
		// padding-right:496px!important;
	}

	h3 {
		font-size:18px;
	}

	.v-select__selections {
		// padding:4px 0 4px 0!important;

		.v-select__selection {
			font-size:14px;
			line-height:16px;
			margin-top:0px!important;
			margin-bottom:0px!important;
			padding:0!important;
		}
		.v-select__selection--comma {
			// margin-top:4px!important;
		}

		input {
			padding:0;
			margin:0;
		}
	}
}

.k-scram-job-dialog-embedded {
	.v-card__text {
		padding-left:16px!important;
		// padding-right:16px!important;
		padding-right:472px!important;
	}
}

.k-scram-job-dialog-fullscreen {
	.v-card {
		border-radius:0!important;
	}

	.v-card__text {
		padding-left:16px!important;
		// padding-right:16px!important;
		padding-right:472px!important;
	}
}

.k-scram-job-framework-title, .k-scram-job-branch-title {
	border:1px solid #666;
	padding:4px 8px;
	font-size:14px;
	line-height:16px;
	border-radius:4px;
	max-width:264px;
}

.k-scram-job-framework-title {
	background-color:#eee;
}

.k-scram-job-branch-title {
	background-color:#ccc;
}

.k-scram-job-guid-field {
	font-size:10px;
	max-width:160px;
	input {
		font-family:monospace;
	}
}

.k-scram-job-status {
	font-size:18px;
	line-height:21px;
}
</style>
